import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import listingService from '../services/listingService';
import marcaService from '../services/marcaService';
import LoginModal from '../components/LoginModal';
import '../sass/MisPublicacionesPage.scss';

const YEARS = (() => {
  const y = [];
  for (let i = new Date().getFullYear(); i >= 1930; i--) y.push(i);
  return y;
})();

function formatPrice(p) {
  if (!p) return '—';
  return '$' + Number(p).toLocaleString('es-CL');
}

// ── Modal de edición ──────────────────────────────────────────────────────────
function EditModal({ listing, makers, onSave, onClose }) {
  const [form, setForm] = useState({
    makerName:    listing.makerName    || '',
    modelName:    listing.modelName    || '',
    year:         listing.year         || '',
    condition:    listing.condition    || 'USED',
    mileage:      listing.mileage      || '',
    transmission: listing.transmission || '',
    fuelType:     listing.fuelType     || '',
    price:        listing.price        || '',
    region:       listing.region       || '',
    city:         listing.city         || '',
    contactName:  listing.contactName  || '',
    contactPhone: listing.contactPhone || '',
    description:  listing.description  || '',
  });
  const [models, setModels] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleMakerChange = (e) => {
    const name = e.target.value;
    const selected = makers.find(m => m.name === name);
    setForm(prev => ({ ...prev, makerName: name, modelName: '' }));
    setModels(selected?.models || []);
  };

  useEffect(() => {
    if (listing.makerName) {
      const selected = makers.find(m => m.name === listing.makerName);
      setModels(selected?.models || []);
    }
  }, [makers, listing.makerName]);

  const handleSave = async () => {
    if (!form.price || !form.contactName || !form.contactPhone) {
      setError('Precio, nombre y teléfono son obligatorios.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave({
        makerName:    form.makerName    || null,
        modelName:    form.modelName    || null,
        year:         form.year         ? Number(form.year) : null,
        condition:    form.condition,
        mileage:      form.mileage      ? Number(form.mileage) : null,
        transmission: form.transmission || null,
        fuelType:     form.fuelType     || null,
        price:        Number(form.price),
        region:       form.region       || null,
        city:         form.city         || null,
        contactName:  form.contactName,
        contactPhone: form.contactPhone,
        description:  form.description  || null,
      });
    } catch {
      setError('No se pudo guardar. Intenta de nuevo.');
      setSaving(false);
    }
  };

  return (
    <div className="mis-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="mis-edit-modal">
        <button className="mis-close" onClick={onClose}>✕</button>
        <h3>Editar publicación</h3>

        <div className="edit-row">
          <div className="edit-field">
            <label>Condición</label>
            <div className="cond-toggle">
              <button className={form.condition === 'USED' ? 'sel' : ''} onClick={() => set('condition', 'USED')}>Usado</button>
              <button className={form.condition === 'NEW'  ? 'sel' : ''} onClick={() => set('condition', 'NEW')}>Nuevo</button>
            </div>
          </div>
        </div>

        <div className="edit-row">
          <div className="edit-field">
            <label>Marca</label>
            <select value={form.makerName} onChange={handleMakerChange}>
              <option value="">Selecciona...</option>
              {makers.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
              <option value="otro">Otro</option>
            </select>
          </div>
          <div className="edit-field">
            <label>Modelo</label>
            <select value={form.modelName} onChange={e => set('modelName', e.target.value)} disabled={!form.makerName}>
              <option value="">Selecciona...</option>
              {models.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
            </select>
          </div>
        </div>

        <div className="edit-row">
          <div className="edit-field">
            <label>Año</label>
            <select value={form.year} onChange={e => set('year', e.target.value)}>
              <option value="">Selecciona...</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
          <div className="edit-field">
            <label>Kilómetros</label>
            <input type="number" min="0" value={form.mileage} onChange={e => set('mileage', e.target.value)} placeholder="ej: 45000" />
          </div>
        </div>

        <div className="edit-row">
          <div className="edit-field">
            <label>Transmisión</label>
            <select value={form.transmission} onChange={e => set('transmission', e.target.value)}>
              <option value="">Selecciona...</option>
              <option>Manual</option>
              <option>Automático</option>
            </select>
          </div>
          <div className="edit-field">
            <label>Combustible</label>
            <select value={form.fuelType} onChange={e => set('fuelType', e.target.value)}>
              <option value="">Selecciona...</option>
              <option>Bencina</option>
              <option>Diésel</option>
              <option>Híbrido</option>
              <option>Eléctrico</option>
              <option>GLP / GNC</option>
            </select>
          </div>
        </div>

        <div className="edit-row">
          <div className="edit-field full">
            <label>Precio (CLP $)</label>
            <input type="number" min="0" value={form.price} onChange={e => set('price', e.target.value)} placeholder="ej: 8500000" />
          </div>
        </div>

        <div className="edit-row">
          <div className="edit-field">
            <label>Nombre contacto</label>
            <input type="text" value={form.contactName} onChange={e => set('contactName', e.target.value)} />
          </div>
          <div className="edit-field">
            <label>Teléfono</label>
            <input type="tel" value={form.contactPhone} onChange={e => set('contactPhone', e.target.value)} />
          </div>
        </div>

        <div className="edit-row">
          <div className="edit-field full">
            <label>Descripción</label>
            <textarea value={form.description} onChange={e => set('description', e.target.value)} rows={3} />
          </div>
        </div>

        {error && <div className="edit-error">{error}</div>}

        <div className="edit-actions">
          <button className="btn-cancel" onClick={onClose}>Cancelar</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function MisPublicacionesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [listings, setListings] = useState([]);
  const [makers, setMakers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);     // listing en edición
  const [deleting, setDeleting] = useState(null);   // id en proceso de borrado
  const [showLogin, setShowLogin] = useState(false);

  const load = useCallback(async (uid) => {
    setLoading(true);
    try {
      const data = await listingService.getMyListings(uid);
      setListings(data);
    } catch {
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    marcaService.getMakers().then(setMakers).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.uid) load(user.uid);
    else if (user === null) setLoading(false); // no logueado
  }, [user, load]);

  const handleSave = async (data) => {
    await listingService.updateListing(editing.id, data);
    setEditing(null);
    load(user.uid);
  };

  const handleDelete = async (id) => {
    setDeleting(id);
    try {
      await listingService.deleteListing(id);
      setListings(prev => prev.filter(l => l.id !== id));
    } finally {
      setDeleting(null);
    }
  };

  // No logueado
  if (user === null) {
    return (
      <div className="mis-page">
        {showLogin && <LoginModal onClose={() => setShowLogin(false)} onSuccess={() => setShowLogin(false)} />}
        <div className="mis-empty">
          <div className="mis-empty-icon">🔐</div>
          <h3>Inicia sesión para ver tus publicaciones</h3>
          <button className="btn-mis-primary" onClick={() => setShowLogin(true)}>Iniciar sesión</button>
        </div>
      </div>
    );
  }

  // Cargando
  if (loading) {
    return (
      <div className="mis-page">
        <div className="mis-empty"><div className="mis-spinner" /></div>
      </div>
    );
  }

  return (
    <div className="mis-page">
      {editing && (
        <EditModal
          listing={editing}
          makers={makers}
          onSave={handleSave}
          onClose={() => setEditing(null)}
        />
      )}

      <div className="mis-header">
        <div>
          <h2>Mis publicaciones</h2>
          <p>{listings.length === 0 ? 'Aún no tienes publicaciones.' : `${listings.length} publicación${listings.length !== 1 ? 'es' : ''}`}</p>
        </div>
        <button className="btn-mis-primary" onClick={() => navigate('/publicar')}>+ Publicar auto</button>
      </div>

      {listings.length === 0 ? (
        <div className="mis-empty">
          <div className="mis-empty-icon">🚗</div>
          <h3>Aún no tienes publicaciones</h3>
          <p>Publica tu auto gratis y llegá a miles de compradores en Chile.</p>
          <button className="btn-mis-primary" onClick={() => navigate('/publicar')}>Publicar mi auto gratis</button>
        </div>
      ) : (
        <div className="mis-list">
          {listings.map(l => (
            <div key={l.id} className="mis-card">
              {l.imageUrls?.length > 0
                ? <img src={`${process.env.REACT_APP_API_URL || 'http://localhost:8080'}${l.imageUrls[0]}`} alt="auto" className="mis-thumb" />
                : <div className="mis-thumb mis-thumb-empty">🚗</div>
              }
              <div className="mis-card-info">
                <div className="mis-card-title">
                  {[l.makerName, l.modelName, l.year].filter(Boolean).join(' ') || 'Sin título'}
                </div>
                <div className="mis-card-price">{formatPrice(l.price)}</div>
                <div className="mis-card-meta">
                  {l.condition === 'USED' ? 'Usado' : 'Nuevo'}
                  {l.mileage  ? ` · ${Number(l.mileage).toLocaleString('es-CL')} km` : ''}
                  {l.city     ? ` · ${l.city}` : ''}
                </div>
              </div>
              <div className="mis-card-actions">
                <button className="btn-edit" onClick={() => setEditing(l)}>Editar</button>
                <button
                  className="btn-delete"
                  disabled={deleting === l.id}
                  onClick={() => window.confirm('¿Eliminar esta publicación?') && handleDelete(l.id)}
                >
                  {deleting === l.id ? '...' : 'Eliminar'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
